import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invitationsApi, usersApi, type User } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDate, formatDateTime } from '@/lib/utils';
import { Search, Mail, Shield, Clock, MailPlus, X } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/lib/auth-context';
import { InviteUserDialog } from '@/components/invite-user-dialog';
import { EditUserDialog } from '@/components/edit-user-dialog';
import { QueryState } from '@/components/query-state';

function UsersSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-full bg-muted" />
              <div className="space-y-2">
                <div className="h-4 w-32 bg-muted rounded" />
                <div className="h-3 w-48 bg-muted rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [revokeError, setRevokeError] = useState<{ id: string; message: string } | null>(null);
  const permissions = usePermissions();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const usersQuery = useQuery({
    queryKey: ['users', search],
    queryFn: () => usersApi.list({ query: search || undefined }),
  });

  const invitationsQuery = useQuery({
    queryKey: ['invitations', currentUser?.tenantId],
    queryFn: () => invitationsApi.listForTenant(currentUser!.tenantId),
    enabled: !!currentUser?.tenantId && permissions.canViewUsers,
  });

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => invitationsApi.revoke(invitationId),
    onSuccess: () => {
      setRevokeError(null);
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
    onError: (err: Error, invitationId) => {
      setRevokeError({
        id: invitationId,
        message: err.message || 'Failed to revoke invitation.',
      });
    },
  });

  const handleRevoke = (invitationId: string) => {
    setRevokeError(null);
    revokeMutation.mutate(invitationId);
  };

  const users = usersQuery.data?.data || [];
  const invitations = invitationsQuery.data?.data || [];

  // Filter pending invitations by the same search term (email only)
  const filteredInvitations = search
    ? invitations.filter((inv) => inv.email.toLowerCase().includes(search.toLowerCase()))
    : invitations;

  const isLoading = usersQuery.isLoading || invitationsQuery.isLoading;
  const isError = usersQuery.isError;
  const isEmpty = users.length === 0 && filteredInvitations.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <span className="rf3-eyebrow">Administration</span>
          <h2 className="text-3xl font-bold tracking-tight text-primary">Users</h2>
          <p className="text-muted-foreground">
            Manage users, roles, and app access
          </p>
        </div>
        {permissions.canInviteUsers && <InviteUserDialog />}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <QueryState
        isLoading={isLoading}
        isError={isError}
        error={usersQuery.error}
        isEmpty={isEmpty}
        skeleton={<UsersSkeleton />}
        emptyMessage="No users or pending invitations found"
      >
        <div className="space-y-3">
          {filteredInvitations.map((invitation) => {
            const isRevokingThis =
              revokeMutation.isPending && revokeMutation.variables === invitation.id;
            const rowError =
              revokeError && revokeError.id === invitation.id
                ? revokeError.message
                : null;
            return (
              <Card
                key={`inv-${invitation.id}`}
                className="border-yellow-200 bg-yellow-50/40"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <MailPlus className="h-5 w-5 text-yellow-700" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{invitation.email}</span>
                          <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                            pending invitation
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Invited {formatDate(invitation.createdAt)} · expires{' '}
                          {formatDate(invitation.expiresAt)}
                        </div>
                      </div>
                    </div>
                    {permissions.canInviteUsers && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRevoke(invitation.id)}
                        disabled={isRevokingThis}
                      >
                        <X className="mr-1 h-3 w-3" />
                        {isRevokingThis ? 'Revoking…' : 'Revoke'}
                      </Button>
                    )}
                  </div>
                  {rowError && (
                    <div className="mt-3 text-sm text-destructive" role="alert">
                      {rowError}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {users.map((user) => {
            const initials =
              user.displayName
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase() || user.email[0].toUpperCase();

            return (
              <Card
                key={user.id}
                className="hover:border-primary/50 transition-colors"
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Avatar>
                        <AvatarImage src={user.avatarUrl || undefined} />
                        <AvatarFallback>{initials}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {user.displayName || 'Unnamed User'}
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              user.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : user.status === 'suspended'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {user.status.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <div className="flex gap-1">
                          {user.roles.length > 0 ? (
                            user.roles.map((role) => (
                              <span
                                key={role.id}
                                className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs"
                              >
                                {role.name}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-xs">
                              No roles
                            </span>
                          )}
                        </div>
                      </div>

                      {user.lastLoginAt && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span className="text-xs">
                            Last login: {formatDateTime(user.lastLoginAt)}
                          </span>
                        </div>
                      )}

                      {permissions.canManageUsers && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingUser(user)}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </QueryState>

      <EditUserDialog
        user={editingUser}
        open={!!editingUser}
        onOpenChange={(open) => {
          if (!open) setEditingUser(null);
        }}
      />
    </div>
  );
}
